import React, { useState, useEffect, useRef } from 'react';
import { cn, getProfilePictureUrl } from '@/utils/utils';
import { UserProfile } from '@/contexts/AuthContext';
import { LogOut, User } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface UserProfileButtonProps {
  userProfile: UserProfile | null;
  handleLogout?: () => void;
  initiateGoogleLogin?: () => void;
  buttonStyle?: 'circle' | 'standard';
  className?: string;
}

export default function UserProfileButton({
  userProfile,
  handleLogout,
  initiateGoogleLogin,
  buttonStyle = 'circle',
  className
}: UserProfileButtonProps) {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [imgLoading, setImgLoading] = useState(true);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Reset image error state when userProfile changes
  useEffect(() => {
    if (userProfile?.picture) {
      setImgError(false);
      setImgLoading(true);
    }
  }, [userProfile?.picture]);
  
  // Check for mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Handle click outside to close user menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };
    
    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);
  
  // Handle user menu toggle
  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
  };
  
  // Handle logout with confirmation
  const handleUserLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      handleLogout?.();
    }
    setShowUserMenu(false);
  };
  
  // Get profile picture URL with fallback
  const profilePicUrl = userProfile ? 
    getProfilePictureUrl(userProfile.picture, userProfile.email) : 
    null;
  
  if (!userProfile) {
    return initiateGoogleLogin ? (
      <Button
        variant={isMobile ? "ghost" : "outline"}
        className={cn(
          "flex items-center gap-2 px-4 py-2 h-full",
          isMobile 
            ? "rounded-none border-b-2 border-transparent hover:border-gray-300" 
            : "rounded-md border border-blue-500 text-blue-500 hover:bg-blue-50",
          className
        )}
        onClick={initiateGoogleLogin}
      >
        <div className="relative">
          <img src="/google-icon.svg" alt="Google" className="w-5 h-5" />
          <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
        </div>
        <span className="font-medium">Sign in with Google</span>
      </Button>
    ) : null;
  }
  
  return (
    <div className={cn("relative", className)} ref={userMenuRef}>
      {buttonStyle === 'circle' ? (
        <Button
          variant="ghost"
          className={cn(
            "flex items-center justify-center p-1 rounded-full h-9 w-9 border border-transparent hover:bg-gray-100",
            showUserMenu && "bg-gray-100 border-gray-200"
          )}
          onClick={toggleUserMenu}
          title={userProfile.name}
        >
          <div className="relative h-7 w-7 rounded-full overflow-hidden border border-gray-200 flex items-center justify-center bg-gray-50">
            {profilePicUrl && !imgError ? (
              <img 
                src={profilePicUrl} 
                alt={userProfile.name} 
                className={cn(
                  "h-full w-full object-cover transition-opacity duration-200",
                  imgLoading ? "opacity-0" : "opacity-100"
                )}
                onLoad={() => setImgLoading(false)}
                onError={() => {
                  console.error("Failed to load profile image:", profilePicUrl);
                  setImgError(true);
                  setImgLoading(false);
                }}
                referrerPolicy="no-referrer"
              />
            ) : null}
            
            {/* Show user icon as fallback or while loading */}
            {(imgError || !profilePicUrl || imgLoading) && (
              <User className="h-4 w-4 text-gray-600" />
            )}
          </div>
        </Button>
      ) : (
        <Button
          variant="ghost"
          className={cn(
            "flex items-center gap-2 px-3 py-1 rounded hover:bg-gray-100",
            showUserMenu && "bg-gray-100"
          )}
          onClick={toggleUserMenu}
        >
          <div className="relative h-7 w-7 rounded-full overflow-hidden border border-gray-200 flex items-center justify-center bg-gray-50">
            {profilePicUrl && !imgError ? (
              <img 
                src={profilePicUrl} 
                alt={userProfile.name} 
                className={cn(
                  "h-full w-full object-cover transition-opacity duration-200",
                  imgLoading ? "opacity-0" : "opacity-100"
                )}
                onLoad={() => setImgLoading(false)}
                onError={() => {
                  console.error("Failed to load profile image:", profilePicUrl);
                  setImgError(true);
                  setImgLoading(false);
                }}
                referrerPolicy="no-referrer"
              />
            ) : null}
            
            {/* Show user icon as fallback or while loading */}
            {(imgError || !profilePicUrl || imgLoading) && (
              <User className="h-4 w-4 text-gray-600" />
            )}
          </div>
        </Button>
      )}
      
      {/* User dropdown menu */}
      {showUserMenu && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
          <div className="p-3 border-b border-gray-100 flex items-center gap-3">
            <div className="relative h-10 w-10 rounded-full overflow-hidden border border-gray-200 flex items-center justify-center bg-gray-50">
              {profilePicUrl && !imgError ? (
                <img 
                  src={profilePicUrl} 
                  alt={userProfile.name} 
                  className="h-full w-full object-cover"
                  onError={() => setImgError(true)}
                  referrerPolicy="no-referrer"
                />
              ) : (
                <User className="h-5 w-5 text-gray-600" />
              )}
            </div>
            <div>
              <div className="text-sm font-medium">{userProfile.name}</div>
              <div className="text-xs text-gray-500 truncate">{userProfile.email}</div>
            </div>
          </div>
          <button 
            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50 flex items-center gap-2"
            onClick={handleUserLogout}
          >
            <LogOut className="h-4 w-4" />
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
} 